import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ProjectOverviewLink } from "@/components/ProjectOverviewLink";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { Table, type Column } from "@/components/ui/Table";
import { ApiError } from "@/lib/apiClient";
import { currentYear } from "@/lib/auswertungen";
import {
  fetchDocumentMarginsReport,
  formatDateShortDe,
  formatDateTimeDe,
  formatEuroCompact,
  formatEuroDe,
  formatPercentDe,
  type DocumentMarginItem,
  type DocumentMarginsReport as DocumentMarginsReportData,
} from "@/lib/heroDocumentReports";
import { MonthlyStackedBars } from "./MonthlyStackedBars";
import { ReportYearPicker } from "./ReportYearPicker";
import "./auswertungen.css";

type DocTyp = "rechnung" | "angebot";

function margeClass(percent: number | null): string {
  if (percent == null) return "text-label-tertiary";
  if (percent < 0) return "eh-docreport-amount--open font-semibold";
  if (percent < 10) return "eh-docreport-overdue--mild font-medium";
  return "eh-docreport-amount--paid font-medium";
}

export function DocumentMarginsReport() {
  const [year, setYear] = useState(currentYear);
  const [typ, setTyp] = useState<DocTyp>("rechnung");
  const [report, setReport] = useState<DocumentMarginsReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchDocumentMarginsReport(year, typ);
      setReport(data);
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Marge-Auswertung konnte nicht geladen werden.",
      );
    } finally {
      setLoading(false);
    }
  }, [year, typ]);

  useEffect(() => {
    void load();
  }, [load]);

  const summary = report?.summary;
  const coverage = report?.coverage;
  const chartData = useMemo(
    () =>
      (report?.by_month ?? []).map((m) => ({
        month: m.month,
        values: {
          lohn: m.lohn,
          material: m.material,
          rest: Math.max(m.netto - m.lohn - m.material, 0),
        },
      })),
    [report?.by_month],
  );

  const columns = useMemo<Column<DocumentMarginItem>[]>(
    () => [
      {
        key: "document_number",
        header: typ === "rechnung" ? "Rechnung" : "Angebot",
        render: (row) => (
          <div>
            <span className="font-medium text-label">{row.document_number || "–"}</span>
            {row.betreff ? (
              <div className="max-w-[16rem] truncate text-xs text-label-secondary">
                {row.betreff}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        key: "kunde",
        header: "Kunde / Projekt",
        render: (row) => (
          <div>
            <div>{row.kunde || "Unbekannt"}</div>
            {row.auftrag_id ? (
              <ProjectOverviewLink
                auftragId={row.auftrag_id}
                label={[row.auftragsnummer, row.beschreibung].filter(Boolean).join(" – ")}
              />
            ) : null}
          </div>
        ),
      },
      {
        key: "doc_date",
        header: "Datum",
        render: (row) => <span className="tabular-nums">{formatDateShortDe(row.doc_date)}</span>,
      },
      {
        key: "netto",
        header: "Netto",
        render: (row) => <span className="tabular-nums font-medium">{formatEuroDe(row.netto)}</span>,
      },
      {
        key: "lohn",
        header: "Lohn",
        render: (row) => <span className="tabular-nums">{formatEuroDe(row.lohn)}</span>,
      },
      {
        key: "material",
        header: "Material",
        render: (row) => <span className="tabular-nums">{formatEuroDe(row.material)}</span>,
      },
      {
        key: "basis_kosten",
        header: "Basiskosten",
        render: (row) =>
          row.basis_kosten != null ? (
            <span className="tabular-nums">{formatEuroDe(row.basis_kosten)}</span>
          ) : (
            <span className="text-label-tertiary">–</span>
          ),
      },
      {
        key: "marge",
        header: "Marge",
        render: (row) =>
          row.marge_percent != null ? (
            <span className={clsx("tabular-nums", margeClass(row.marge_percent))}>
              {formatEuroDe(row.marge_amount)} ({String(row.marge_percent).replace(".", ",")} %)
            </span>
          ) : (
            <span className="text-label-tertiary">–</span>
          ),
      },
    ],
    [typ],
  );

  const coverageMissing =
    (coverage?.documents_total ?? 0) - (coverage?.documents_with_details ?? 0);

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="error" title="Marge-Auswertung konnte nicht geladen werden">
          {error}
        </Alert>
      )}

      <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35">
        <ReportYearPicker
          year={year}
          onYearChange={setYear}
          loading={loading}
          onReload={() => void load()}
        />
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex rounded-lg border border-separator p-0.5">
            <button
              type="button"
              className={clsx(
                "rounded-md px-3 py-1.5 text-sm transition",
                typ === "rechnung" ? "bg-accent text-white" : "text-label-secondary hover:text-label",
              )}
              onClick={() => setTyp("rechnung")}
            >
              Rechnungen
            </button>
            <button
              type="button"
              className={clsx(
                "rounded-md px-3 py-1.5 text-sm transition",
                typ === "angebot" ? "bg-accent text-white" : "text-label-secondary hover:text-label",
              )}
              onClick={() => setTyp("angebot")}
            >
              Angebote
            </button>
          </div>
          <p className="text-xs text-label-secondary">
            Lohn/Material-Aufteilung und Marge aus den synchronisierten Hero-Detaildaten.
            {report?.last_synced_at
              ? ` Letzter Sync: ${formatDateTimeDe(report.last_synced_at)}.`
              : ""}
          </p>
        </div>
      </section>

      {loading && !report ? (
        <div className="flex items-center justify-center py-16 text-label-secondary">
          <Spinner />
          <span className="ml-2 text-sm">Marge-Auswertung wird geladen …</span>
        </div>
      ) : report ? (
        <>
          {coverageMissing > 0 ? (
            <Alert variant="info" title="Unvollständige Detaildaten">
              Für {coverageMissing} von {coverage?.documents_total ?? 0} Dokumenten sind noch keine
              Hero-Detaildaten synchronisiert – diese fehlen in den Summen und der Tabelle
              (Abdeckung: {formatPercentDe(coverage?.coverage_rate)}).
            </Alert>
          ) : null}

          <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">Netto gesamt</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-label">
                {formatEuroCompact(summary?.netto_sum)}
              </p>
              <p className="mt-1 text-[11px] text-label-tertiary">
                {coverage?.documents_with_details ?? 0} Dokumente mit Details
              </p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">Lohnanteil</p>
              <p className="eh-docreport-amount--lohn mt-1 text-2xl font-semibold tabular-nums">
                {formatEuroCompact(summary?.lohn_sum)}
              </p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">Materialanteil</p>
              <p className="eh-docreport-amount--material mt-1 text-2xl font-semibold tabular-nums">
                {formatEuroCompact(summary?.material_sum)}
              </p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">Marge (mit Basiskosten)</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-label">
                {summary?.marge_amount != null ? formatEuroCompact(summary.marge_amount) : "–"}
              </p>
              <p className="mt-1 text-[11px] text-label-tertiary">
                {summary?.marge_percent != null
                  ? `${String(summary.marge_percent).replace(".", ",")} % auf Basiskosten-Netto`
                  : "Keine Basiskosten vorhanden"}
              </p>
            </div>
          </section>

          <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35">
            <p className="mb-3 text-xs font-medium text-label-secondary">
              Netto pro Monat (Lohn / Material / Sonstiges)
            </p>
            <MonthlyStackedBars
              data={chartData}
              series={[
                { key: "lohn", label: "Lohn", variant: "lohn" },
                { key: "material", label: "Material", variant: "material" },
                { key: "rest", label: "Sonstiges", variant: "rest" },
              ]}
              formatValue={formatEuroCompact}
              emptyText={`Keine Detaildaten für ${year} gefunden.`}
            />
          </section>

          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-label">
              Dokumente mit Detaildaten ({report.items.length})
            </h3>
            <Table
              columns={columns}
              rows={report.items}
              rowKey={(row) => String(row.hero_document_id)}
              emptyText={`Keine Detaildaten für ${year} gefunden.`}
            />
          </section>
        </>
      ) : null}
    </div>
  );
}
